import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, Plus, User, Users } from 'lucide-react';
import {
  isRefusal,
  studentMessagingApi,
  studentMessagingError,
  studentMessagingKeys,
  type StudentConversation,
} from '../api/studentMessaging';
import {
  ErrorState,
  KIND_LABELS,
  Loading,
  OFFLINE,
  RefreshNotice,
  UnreadBadge,
  card,
  primaryButton,
  secondaryButton,
} from '../components/StudentMessageParts';
import {
  NewDirectPanel,
  NewGroupPanel,
} from '../components/StudentMessageStart';
import StudentMessageThread from '../components/StudentMessageThread';
import { useStudentConversations } from '../hooks/useStudentConversations';
import { useAuthStore } from '../store/auth';
import dayjs from '../lib/dates';

type Pane =
  | { type: 'thread'; id: string }
  | { type: 'direct' }
  | { type: 'group' }
  | null;

function listStamp(iso: string): string {
  const date = dayjs(iso);
  const now = dayjs();
  if (date.isSame(now, 'day')) return date.format('HH:mm');
  return date.format(date.isSame(now, 'year') ? 'D MMM' : 'DD/MM/YYYY');
}

function ConversationRow({
  conversation,
  active,
  onOpen,
}: {
  conversation: StudentConversation;
  active: boolean;
  onOpen: () => void;
}) {
  const { kind, title, unread, last } = conversation;
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        aria-current={active ? 'true' : undefined}
        className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${
          active ? 'bg-indigo-50' : 'hover:bg-slate-50'
        }`}
      >
        <span
          aria-hidden="true"
          className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0"
        >
          {kind === 'GROUP' ? <Users size={18} /> : <User size={18} />}
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2">
            <span
              className={`truncate text-sm ${
                unread > 0
                  ? 'font-semibold text-slate-900'
                  : 'font-medium text-slate-800'
              }`}
            >
              {title}
            </span>
            <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium">
              {KIND_LABELS[kind] ?? 'Échange'}
            </span>
          </span>
          <span className="block truncate text-xs text-slate-500 mt-0.5">
            {last ? `${last.author} : ${last.body}` : 'Aucun message'}
          </span>
        </span>
        <span className="flex flex-col items-end gap-1 flex-shrink-0">
          {last && (
            <span className="text-[11px] text-slate-400">
              {listStamp(last.at)}
            </span>
          )}
          <UnreadBadge count={unread} />
        </span>
      </button>
    </li>
  );
}

/**
 * Échanges du moniteur avec les élèves : liste, fil, nouvel échange à deux et
 * nouveau groupe. La messagerie interne de l'équipe reste dans MessagesView.
 */
export default function StudentMessagesView() {
  const userId = useAuthStore((s) => s.user?.id);
  const qc = useQueryClient();
  const [pane, setPane] = useState<Pane>(null);
  const list = useStudentConversations();
  const contacts = useQuery({
    queryKey: studentMessagingKeys.contacts(userId),
    queryFn: studentMessagingApi.getContacts,
    staleTime: 0,
    retry: false,
    gcTime: 0,
  });

  // Accès refusé, ou premier chargement en échec : rien d'autre n'est montré.
  const failure =
    list.isError && (!list.data || isRefusal(list.error))
      ? list.error
      : contacts.isError && (!contacts.data || isRefusal(contacts.error))
        ? contacts.error
        : null;
  const loading = !failure && (list.isPending || contacts.isPending);
  const conversations = list.data ?? [];
  const selected = pane?.type === 'thread' ? pane.id : null;

  function openConversation(id: string) {
    void qc.invalidateQueries({
      queryKey: studentMessagingKeys.conversations(userId),
    });
    setPane({ type: 'thread', id });
  }

  if (failure) {
    return (
      <section className="space-y-4">
        <h1 className="text-slate-900">Messages élèves</h1>
        <div className={card}>
          <ErrorState
            message={studentMessagingError(failure, OFFLINE)}
            onRetry={() => {
              void list.refetch();
              void contacts.refetch();
            }}
          />
        </div>
      </section>
    );
  }

  return (
    <section>
      {/* Sur téléphone, un échange ouvert occupe tout l'écran. */}
      <div
        className={`${
          pane ? 'hidden lg:flex' : 'flex'
        } flex-wrap items-end justify-between gap-3 mb-4`}
      >
        <div>
          <h1 className="text-slate-900">Messages élèves</h1>
          <p className="text-slate-600">
            Vos échanges avec les élèves de votre auto-école.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {contacts.data?.canOpenDirect && (
            <button
              type="button"
              className={primaryButton}
              onClick={() => setPane({ type: 'direct' })}
            >
              <Plus size={16} />
              Nouvel échange
            </button>
          )}
          {contacts.data?.canCreateGroup && (
            <button
              type="button"
              className={secondaryButton}
              onClick={() => {
                // L'équipe a pu changer depuis l'ouverture de l'écran.
                void contacts.refetch();
                setPane({ type: 'group' });
              }}
            >
              <Users size={16} />
              Nouveau groupe
            </button>
          )}
        </div>
      </div>

      <div className="lg:grid lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:items-start lg:gap-5">
        <div
          className={`${card} relative overflow-hidden lg:max-h-[calc(100dvh-13rem)] lg:overflow-y-auto ${
            pane ? 'hidden lg:block' : ''
          }`}
        >
          {loading ? (
            <Loading label="Chargement des échanges…" />
          ) : (
            <>
              {list.isError && (
                <RefreshNotice
                  error={list.error}
                  onRetry={() => void list.refetch()}
                />
              )}
              {conversations.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">
                  <MessageCircle
                    size={28}
                    className="mx-auto mb-3 text-slate-300"
                  />
                  <p className="font-medium text-slate-700">
                    Aucun échange pour le moment.
                  </p>
                  <p className="mt-1">
                    Écrivez à un élève, ou attendez son premier message : il
                    apparaîtra ici.
                  </p>
                </div>
              ) : (
                <ul
                  aria-label="Échanges avec les élèves"
                  className="divide-y divide-slate-100"
                >
                  {conversations.map((conversation) => (
                    <ConversationRow
                      key={conversation.id}
                      conversation={conversation}
                      active={conversation.id === selected}
                      onOpen={() =>
                        setPane({ type: 'thread', id: conversation.id })
                      }
                    />
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        <div className={pane ? '' : 'hidden lg:block'}>
          {pane?.type === 'thread' && (
            <StudentMessageThread
              key={pane.id}
              conversationId={pane.id}
              fallbackTitle={conversations.find((c) => c.id === pane.id)?.title}
              onBack={() => setPane(null)}
            />
          )}
          {pane?.type === 'direct' && (
            <NewDirectPanel
              onClose={() => setPane(null)}
              onOpened={openConversation}
            />
          )}
          {pane?.type === 'group' && (
            <NewGroupPanel
              staff={contacts.data?.staff ?? []}
              onClose={() => setPane(null)}
              onCreated={openConversation}
            />
          )}
          {!pane && (
            <div
              className={`${card} flex flex-col items-center justify-center gap-3 p-10 text-center text-sm text-slate-500`}
            >
              <MessageCircle size={28} className="text-slate-300" />
              Sélectionnez un échange pour lire les messages.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
